/**
 * Адаптер вкладок к оболочке C2: `registerTabs(Tabs)` (проп `tab` из маршрута), игрок — из `appStore`, тексты — i18n.
 *
 * Покупка — колбэк {@link onPurchase} по контракту API (`PurchaseRequest`). Пока сервер не подключён (I2), это
 * {@link optimisticPurchase}: `applyPurchase` из engine к `appStore.player`; I2 заменит его вызовом `purchase` API
 * через {@link connectTabs} и подтвердит ответом сервера (`setPlayer`).
 */
import type { PurchaseRequest } from "@newfungame/contracts";
import { playSound } from "../../audio";
import { applyPurchase } from "@newfungame/engine";
import { activeBalance } from "../../app/balance";
import { useStore, useT } from "../../app/hooks";
import type { TabsViewProps as SlotProps } from "../../app/slots";
import {
  appStore,
  battleStore,
  closeTab,
  coinsShortActive,
  currentWindow,
  setPlayer,
  toggleTab,
} from "../../app/store";
import { addMessages } from "../../i18n";
import { tabsMessages, type TabsT } from "./messages";
import { toTabsModel } from "./model";
import { completeHint, hintsStore, levelHintTarget } from "../hints";
import { TabsView, type TabsActions } from "./TabsView";

addMessages(tabsMessages);

/** Обработчик покупки: оптимистичный клиентский сейчас, серверный (I2) потом. */
export type PurchaseHandler = (req: PurchaseRequest) => void;

/** Применить покупку к игроку локально (без сервера). Отказ (нет монет и т. п.) — ничего не меняет. */
export const optimisticPurchase: PurchaseHandler = (req) => {
  const player = appStore.get().player;
  if (!player) return;
  const r = applyPurchase(activeBalance(), player, req);
  if (r.ok) setPlayer(r.state);
};

let handler: PurchaseHandler = optimisticPurchase;

/** Подключить покупку к серверу (I2). Без подключения — оптимистичная. */
export function connectTabs(onPurchase: PurchaseHandler): void {
  handler = onPurchase;
}

export const tabsActions: TabsActions = {
  onSelect: (tab) => toggleTab(tab),
  onClose: () => closeTab(),
  onPurchase: (req) => {
    if (req.kind === "heroLevel") completeHint("level");
    playSound("purchase");
    handler(req);
  },
};

/** Панель десктопа (C6): тап по вкладке выбирает её, а не переключает «открыто/закрыто»; закрывать нечего. */
export const panelActions: TabsActions = {
  ...tabsActions,
  onSelect: (tab) => appStore.set((s) => (s.route.tab === tab ? s : { ...s, route: { ...s.route, tab } })),
};

export function Tabs({ tab, mode = "sheet" }: SlotProps) {
  const player = useStore(appStore, (s) => s.player);
  const t = useT() as TabsT;
  const hints = useStore(hintsStore, (s) => s);
  const windowOpen = useStore(appStore, (s) => currentWindow(s) !== null);
  const earned = useStore(battleStore, (s) => s?.wallet.earnedThisWave ?? 0);
  const inFlight = useStore(appStore, (s) => s.coinsInFlight);
  const short = useStore(appStore, coinsShortActive);
  if (!player) return null;
  const model = toTabsModel(activeBalance(), player, { earnedThisWave: earned, inFlight, short });
  // в колонке десктопа (C6) вкладки всегда открыты — подсказка нужна только нижней панели телефона
  const hint =
    mode === "panel"
      ? null
      : levelHintTarget({ done: hints.done.level, skillShown: hints.skillShown, windowOpen, tab, rows: model.level });
  return (
    <TabsView
      hint={hint}
      model={model}
      tab={tab}
      mode={mode}
      actions={mode === "panel" ? panelActions : tabsActions}
      t={t}
    />
  );
}
